'use client'

import {useEffect, useRef, useState, type ReactElement} from 'react'
import {withDeadline} from '@/lib/withDeadline'
import {FileText, Download} from 'lucide-react'
import {useIntl} from 'react-intl'
import type {ProductManualDocument} from '@/types/sanity'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import {Button} from '@/components/ui/button'
import {
  filterProductDocumentsByLocale,
  productDocumentTitle,
  sortProductDocuments,
} from '@/components/products/productDisplay'

type ProductDocumentsSheetProps = {
  title: string
  documents: ProductManualDocument[]
  loadDocuments?: (signal?: AbortSignal) => Promise<ProductManualDocument[]>
  description?: string
  locale?: string
  filterByLocale?: boolean
  trigger?: ReactElement
  sheetId?: string
}

type ProductDocumentsListProps = {
  title: string
  documents: ProductManualDocument[]
  locale?: string
  filterByLocale?: boolean
}

const marketLabel = (value: string, locale: string) => {
  const normalized = String(value || '')
    .trim()
    .toUpperCase()
  if (!normalized) return ''
  if (normalized === 'GLOBAL') return 'Global'
  try {
    const formatter = new Intl.DisplayNames([locale], {type: 'region'})
    return formatter.of(normalized) || normalized
  } catch {
    return normalized
  }
}

const normalizeId = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

export function ProductDocumentsSheet({
  title,
  documents,
  loadDocuments,
  description,
  locale = 'en-US',
  filterByLocale = true,
  trigger,
  sheetId,
}: ProductDocumentsSheetProps) {
  const intl = useIntl()
  const [open, setOpen] = useState(false)
  const [loadedDocuments, setLoadedDocuments] = useState<ProductManualDocument[] | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const pendingLoad = useRef<AbortController | null>(null)
  useEffect(() => () => pendingLoad.current?.abort(), [])
  const baseId = normalizeId(sheetId || title || 'documents')
  const resolvedDescription =
    description ??
    intl.formatMessage({
      id: 'product.documentsDescription',
      defaultMessage: 'Browse all manuals and technical documents available for this product.',
    })
  const resolvedDocuments = loadedDocuments || documents

  const runLazyLoad = () => {
    if (!loadDocuments || loadedDocuments || pendingLoad.current) return
    setLoadFailed(false)
    const controller = new AbortController()
    pendingLoad.current = controller
    withDeadline((signal) => loadDocuments(signal), 12000, controller.signal)
      .then((nextDocuments) => {
        if (!controller.signal.aborted) setLoadedDocuments(nextDocuments)
      })
      .catch(() => {
        if (controller.signal.aborted) return
        setLoadFailed(true)
      })
      .finally(() => {
        if (pendingLoad.current === controller) pendingLoad.current = null
      })
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (nextOpen) runLazyLoad()
    else {
      pendingLoad.current?.abort()
      pendingLoad.current = null
    }
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger
        id={`${baseId}-trigger`}
        render={
          trigger || (
            <Button
              className="h-11 w-full justify-center rounded-full px-5"
              size="sm"
              variant="default"
            >
              <FileText className="size-4" />
              {intl.formatMessage({id: 'product.viewDocuments', defaultMessage: 'View Documents'})}
            </Button>
          )
        }
      />
      {open ? (
        <SheetContent
          id={`${baseId}-content`}
          side="right"
          className="!w-[85vw] !max-w-[85vw] overflow-y-auto bg-white p-0 lg:!w-[50vw] lg:!max-w-[50vw]"
        >
          <SheetHeader className="border-b border-border px-6 py-5">
            <SheetTitle
              id={`${baseId}-title`}
              className="text-2xl font-semibold tracking-tight text-foreground"
            >
              {title}
            </SheetTitle>
            <SheetDescription id={`${baseId}-description`}>{resolvedDescription}</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 px-6 py-6">
            {loadDocuments && !loadedDocuments ? (
              loadFailed ? (
                <div role="alert" className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    {intl.formatMessage({
                      id: 'product.documentsLoadError',
                      defaultMessage: 'Couldn’t load the documents. Please try again.',
                    })}
                  </p>
                  <Button variant="outline" size="sm" onClick={runLazyLoad}>
                    {intl.formatMessage({id: 'common.retry', defaultMessage: 'Retry'})}
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {intl.formatMessage({id: 'common.loading', defaultMessage: 'Loading…'})}
                </p>
              )
            ) : (
              <ProductDocumentsList
                title={title}
                documents={resolvedDocuments}
                locale={locale}
                filterByLocale={filterByLocale}
              />
            )}
          </div>
        </SheetContent>
      ) : null}
    </Sheet>
  )
}

export function ProductDocumentsList({
  title,
  documents,
  locale = 'en-US',
  filterByLocale = true,
}: ProductDocumentsListProps) {
  const intl = useIntl()
  const fallbackTitle = intl.formatMessage({
    id: 'product.documentFallbackTitle',
    defaultMessage: 'Document',
  })
  const notDefined = intl.formatMessage({
    id: 'common.notDefined',
    defaultMessage: 'Not defined',
  })
  const marketsLabelText = intl.formatMessage({
    id: 'product.documentMarketsLabel',
    defaultMessage: 'Markets',
  })
  const visibleDocuments = filterByLocale
    ? filterProductDocumentsByLocale(documents, locale)
    : documents
  const sortedDocuments = sortProductDocuments(visibleDocuments, locale)
  if (!sortedDocuments.length)
    return (
      <p>
        {intl.formatMessage({
          id: 'product.documentsEmpty',
          defaultMessage: 'No documents available.',
        })}
      </p>
    )
  return (
    <>
      {sortedDocuments.map((document) => (
        <a
          key={
            document._id || document._key || document.mediaCode || document.url || document.filename
          }
          href={document.url}
          target="_blank"
          rel="noreferrer"
          aria-label={`${intl.formatMessage({id: 'product.downloadLabel', defaultMessage: 'Download'})} ${document.filename || document.title || fallbackTitle}`}
          className="flex items-start justify-between gap-4 rounded-card border border-border px-4 py-4 !no-underline transition-colors hover:border-border hover:bg-muted/35 hover:!no-underline [&_*]:!no-underline"
        >
          <div className="min-w-0 space-y-1 break-words">
            <h3 className="text-base font-semibold text-foreground">
              {productDocumentTitle(document, locale, fallbackTitle)}
            </h3>
            {/* Article number intentionally hidden (client request, JO2 2026-07): the imported
                productNumber is the fictive BASE/mother PIM number rather than the actual item,
                and the client has since confirmed the real number can differ per market — it was
                generating dealer support tickets. Same removal as ProductCard / ProductDetailHero
                / ProductDetailTabs (#680); this drawer renders on the product detail page too and
                was missed there. Re-enable (or add a CMS visibility toggle) only once PIM exposes
                a real per-item, per-market number. */}
            <p className="text-sm text-muted-foreground">{document.productTitle || title}</p>
            <p className="text-xs text-muted-foreground">
              {marketsLabelText}:{' '}
              {(document.marketKeys || []).length
                ? (document.marketKeys || []).map((item) => marketLabel(item, locale)).join(', ')
                : notDefined}
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-primary">
            <Download className="size-4" />
            {intl.formatMessage({id: 'product.downloadLabel', defaultMessage: 'Download'})}
          </span>
        </a>
      ))}
    </>
  )
}
